export type TypePhase = 'type' | 'hold' | 'erase' | 'gap';

export type TypeCycleState = {
  phraseIndex: number;
  charCount: number;
  phase: TypePhase;
};

export const TYPE_MS = 78;
export const HOLD_MS = 3600;
export const ERASE_MS = 52;
export const GAP_MS = 380;

export function nextTypeCycle(
  state: TypeCycleState,
  phraseCount: number,
  phraseLength: number,
): { state: TypeCycleState; delayMs: number } {
  if (phraseCount < 1) return { state, delayMs: HOLD_MS };
  if (state.phase === 'type') {
    if (state.charCount >= phraseLength) return { state: { ...state, phase: 'hold' }, delayMs: HOLD_MS };
    return { state: { ...state, charCount: state.charCount + 1 }, delayMs: TYPE_MS };
  }
  if (state.phase === 'hold') return { state: { ...state, phase: 'erase' }, delayMs: ERASE_MS };
  if (state.phase === 'erase') {
    if (state.charCount <= 0) {
      return {
        state: { phraseIndex: (state.phraseIndex + 1) % phraseCount, charCount: 0, phase: 'gap' },
        delayMs: GAP_MS,
      };
    }
    return { state: { ...state, charCount: state.charCount - 1 }, delayMs: ERASE_MS };
  }
  return { state: { ...state, phase: 'type' }, delayMs: TYPE_MS };
}

export function slicePhrase(full: string, charCount: number): string {
  return full.slice(0, Math.max(0, charCount));
}

export function visibleParts(
  prefix: string,
  accent: string,
  suffix: string,
  charCount: number,
): { prefix: string; accent: string; suffix: string } {
  const visible = slicePhrase(prefix + accent + suffix, charCount);
  const accentAt = prefix.length;
  const suffixAt = accentAt + accent.length;
  return {
    prefix: visible.slice(0, Math.min(visible.length, accentAt)),
    accent: visible.length <= accentAt ? '' : visible.slice(accentAt, Math.min(visible.length, suffixAt)),
    suffix: visible.length <= suffixAt ? '' : visible.slice(suffixAt),
  };
}

/**
 * WU-HERO: the line under the headline follows the typing. It is shown while a headline types and holds,
 * fades out (moving up) as it erases, and waits below, invisible, through the gap, so the next line rises in.
 */
export type LeadState = 'in' | 'out' | 'wait';

export function leadState(phase: TypePhase): LeadState {
  if (phase === 'erase') return 'out';
  if (phase === 'gap') return 'wait';
  return 'in';
}
